import { useCallback, useEffect, useState } from 'react'
import apiClient from '../api/client'
import { useAuth } from '../auth/useAuth'
import ErrorAlert from '../components/ErrorAlert'
import LoadingSpinner from '../components/LoadingSpinner'

interface Config {
  HUBSPOT_WRITE_MODE?: string
  OUTREACH_MODE?: string
  WEBHOOK_ENABLED?: string
  DRIVE_EXPORT_MODE?: string
}

interface DriveState {
  enabled: boolean
  folder_id: string
  folder_name: string
  folder_url: string
  sa_email: string
  last_check_ts: string
  last_check_status: string
  updated_by: string
  updated_at: string
  /** Con qué cuenta sube el bot. Ver `lambdas/shared/drive_auth.py`. */
  auth_mode: 'service_account' | 'oauth'
  oauth_email: string
  oauth_conectado_at: string
  oauth_conectado_por: string
  /** Solo si Google le puso fecha de vencimiento al acceso (app en «prueba»). */
  oauth_refresh_vence_at: string
  /** Google rechazó el token: hay que reconectar. */
  oauth_error: string
  oauth_error_at: string
  /** La dirección que tiene que estar registrada en el cliente OAuth. */
  oauth_redirect_uri: string
}

function fechaLegible(iso: string): string {
  if (!iso) return ''
  return new Date(iso).toLocaleString('es-PE', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}

/**
 * La cuenta con la que el bot sube los archivos a Drive.
 *
 * La cuenta de servicio no tiene espacio propio: solo sirve en una unidad
 * compartida. Con una cuenta de Google conectada los archivos son de esa
 * cuenta, y sirve también en una «Mi unidad» —la raíz de PROD—. Si Google
 * invalida el acceso (cambio de contraseña, cuenta suspendida), acá aparece en
 * rojo con el botón para reconectar.
 */
function CuentaQueSube({ drive, isAdmin, ocupado, onConectar, onDesconectar }: {
  drive: DriveState
  isAdmin: boolean
  ocupado: boolean
  onConectar: () => void
  onDesconectar: () => void
}) {
  const conGoogle = drive.auth_mode === 'oauth'
  const conError = conGoogle && Boolean(drive.oauth_error)
  const tono = conError
    ? { fondo: 'var(--c-danger-light)', borde: 'var(--c-danger-border)', texto: 'var(--c-danger-ink)' }
    : conGoogle
      ? { fondo: 'var(--c-brand-light)', borde: 'var(--c-brand-border)', texto: 'var(--c-brand-ink)' }
      : { fondo: 'var(--c-info-tint)', borde: 'var(--c-info-border)', texto: 'var(--c-info-slate)' }
  const boton = (texto: string, onClick: () => void, principal: boolean) => (
    <button onClick={onClick} disabled={ocupado} style={{
      all: 'unset', cursor: ocupado ? 'not-allowed' : 'pointer',
      padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
      border: `1px solid ${principal ? 'var(--c-brand)' : 'var(--c-surface-strong)'}`,
      background: principal ? 'var(--c-brand)' : '#fff',
      color: principal ? '#fff' : 'var(--c-ink-soft)', opacity: ocupado ? 0.6 : 1,
    }}>{ocupado ? '…' : texto}</button>
  )

  return (
    <div style={{ background: tono.fondo, border: `1px solid ${tono.borde}`, borderRadius: 8,
                  padding: '10px 14px', fontSize: 12, color: tono.texto,
                  display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ fontWeight: 700 }}>
        {conGoogle ? 'Sube con una cuenta de Google' : 'Sube con la cuenta de servicio'}
      </div>
      <div style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
        {conGoogle ? drive.oauth_email : (drive.sa_email || 'no disponible')}
      </div>
      {conError ? (
        <div>
          <b>Google ya no autoriza esta cuenta</b> ({fechaLegible(drive.oauth_error_at)}): el bot no
          está archivando. Vuelve a conectar la cuenta.
        </div>
      ) : conGoogle ? (
        <div style={{ opacity: 0.9 }}>
          Conectada el {fechaLegible(drive.oauth_conectado_at)}
          {drive.oauth_conectado_por && ` por ${drive.oauth_conectado_por.split('@')[0]}`}. Los archivos
          quedan a nombre de esta cuenta. Si cambia su contraseña, hay que reconectarla.
          {drive.oauth_refresh_vence_at && (
            <> <b>Google limita este acceso hasta el {fechaLegible(drive.oauth_refresh_vence_at)}</b>:
            la app de Google está en modo «prueba».</>
          )}
        </div>
      ) : (
        <div style={{ opacity: 0.85 }}>
          Comparte la carpeta con este email como Gestor de contenido. No tiene espacio propio:
          solo sirve si la carpeta está en una unidad compartida. Para una «Mi unidad», conecta
          una cuenta de Google.
        </div>
      )}
      {isAdmin && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 2 }}>
          {conGoogle
            ? <>
                {boton('Reconectar', onConectar, conError)}
                {boton('Volver a la cuenta de servicio', onDesconectar, false)}
              </>
            : boton('Conectar cuenta de Google', onConectar, true)}
        </div>
      )}
      {isAdmin && !conGoogle && drive.oauth_redirect_uri && (
        <div style={{ opacity: 0.75, fontSize: 11 }}>
          En Google Cloud, el cliente OAuth del login tiene que tener registrada esta URI de
          redirección: <code style={{ wordBreak: 'break-all' }}>{drive.oauth_redirect_uri}</code>
        </div>
      )}
    </div>
  )
}

function StatusDot({ active }: { active: boolean }) {
  return (
    <span style={{
      display: 'inline-block', width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
      background: active ? 'var(--c-brand)' : 'var(--c-ink-ghost)',
      boxShadow: active ? '0 0 0 3px var(--c-brand-border)' : 'none',
    }} />
  )
}

interface IntegrationCardProps {
  title: string
  description: string
  status: 'active' | 'inactive' | 'warning'
  statusLabel: string
  detail?: string
  children?: React.ReactNode
}

function IntegrationCard({ title, description, status, statusLabel, detail, children }: IntegrationCardProps) {
  const colors = {
    active:   { bg: 'var(--c-brand-light)', color: 'var(--c-brand-ink)' },
    inactive: { bg: 'var(--c-surface-faint)', color: 'var(--c-ink-muted)' },
    warning:  { bg: 'var(--c-warn-light)',  color: 'var(--c-warn)' },
  }
  const c = colors[status]
  return (
    <div style={{
      background: '#fff', border: '1px solid var(--c-surface-border)',
      borderRadius: 12, padding: '20px 24px',
      display: 'flex', flexDirection: 'column', gap: 14,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--c-ink)' }}>{title}</div>
          <div style={{ fontSize: 13, color: 'var(--c-ink-muted)', marginTop: 3 }}>{description}</div>
        </div>
        <span style={{
          padding: '3px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700,
          background: c.bg, color: c.color, whiteSpace: 'nowrap', flexShrink: 0,
        }}>
          {statusLabel}
        </span>
      </div>
      {detail && (
        <div style={{ fontSize: 12, color: 'var(--c-ink-muted)', fontFamily: 'monospace', background: 'var(--c-surface-subtle)', padding: '8px 12px', borderRadius: 7 }}>
          {detail}
        </div>
      )}
      {children}
    </div>
  )
}

export default function Integrations() {
  const { isAdmin } = useAuth()
  const [config, setConfig]   = useState<Config>({})
  const [drive, setDrive]     = useState<DriveState | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving]   = useState<string | null>(null)
  const [error, setError]     = useState<string | null>(null)
  const [driveMsg, setDriveMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)

  // Edición del folder de Drive
  const [showDriveEdit, setShowDriveEdit] = useState(false)
  const [newFolderId, setNewFolderId] = useState('')
  const [testing, setTesting] = useState(false)

  const fetchAll = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const [cfg, drv] = await Promise.all([
        apiClient.get<Config>('/config'),
        apiClient.get<DriveState>('/integrations/drive'),
      ])
      setConfig(cfg.data)
      setDrive(drv.data)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar integraciones')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void fetchAll() }, [fetchAll])

  const testDrive = async (folderId?: string, escritura = false) => {
    if (escritura && !window.confirm('Se va a subir un archivo de una línea a la carpeta con la cuenta '
                                     + 'conectada y se va a borrar en el acto. ¿Seguir?')) return
    setTesting(true); setDriveMsg(null)
    try {
      const res = await apiClient.post<{
        ok: boolean; folder_name: string; error?: string; sa_email: string; cuenta?: string
        espacio?: { puede_ser_duena?: boolean }
        escritura?: { ok: boolean; duena?: string; limpieza?: string; error?: string }
      }>(
        '/integrations/drive/test',
        { ...(folderId ? { folder_id: folderId } : {}), ...(escritura ? { escritura: true } : {}) },
      )
      const d = res.data
      if (d.escritura?.ok) {
        setDriveMsg({ kind: 'ok', text: `Subida OK — el archivo de prueba quedó a nombre de `
          + `${d.escritura.duena} en "${d.folder_name}" y se ${d.escritura.limpieza === 'borrado' ? 'borró' : d.escritura.limpieza}.` })
      } else if (d.ok) {
        setDriveMsg({ kind: 'ok', text: `Conexión OK — carpeta "${d.folder_name}"`
          + (d.cuenta ? ` · sube como ${d.cuenta}` : '')
          + (d.espacio && d.espacio.puede_ser_duena === false
            ? ' · ojo: esta cuenta no tiene espacio propio (solo sirve en una unidad compartida)' : '') })
      } else setDriveMsg({ kind: 'err', text: d.escritura?.error ?? d.error ?? 'Error de conexión' })
    } catch (e) {
      const anyErr = e as { response?: { data?: { error?: string; sa_email?: string; hint?: string } } }
      const data = anyErr?.response?.data
      setDriveMsg({ kind: 'err', text: [data?.error, data?.hint].filter(Boolean).join(' — ') || 'Error de conexión' })
    } finally { setTesting(false) }
  }

  const saveDriveFolder = async () => {
    if (!newFolderId.trim()) return
    setSaving('drive'); setDriveMsg(null)
    try {
      const res = await apiClient.put<{ status: string; folder_id: string; folder_name: string }>(
        '/integrations/drive', { folder_id: newFolderId.trim() },
      )
      setDriveMsg({ kind: 'ok', text: `Carpeta actualizada a "${res.data.folder_name}"` })
      setShowDriveEdit(false); setNewFolderId('')
      await fetchAll()
    } catch (e) {
      const anyErr = e as { response?: { data?: { error?: string; hint?: string } } }
      const data = anyErr?.response?.data
      setDriveMsg({ kind: 'err', text: [data?.error, data?.hint].filter(Boolean).join(' — ') || 'Error al guardar' })
    } finally { setSaving(null) }
  }

  // CONECTAR UNA CUENTA DE GOOGLE
  //
  // El inicio de sesión es en la pantalla de Google: el portal solo pide el
  // enlace y manda ahí. Google vuelve a `/integraciones/google` con un código de
  // un solo uso que canjea el backend; el token nunca pasa por el navegador.
  const driveSinAutorizar = drive?.auth_mode === 'oauth' && Boolean(drive?.oauth_error)

  const conectarGoogle = async () => {
    setSaving('drive-oauth'); setDriveMsg(null)
    try {
      const res = await apiClient.post<{ url: string }>('/integrations/drive/oauth/start', {})
      window.location.assign(res.data.url)
    } catch (e) {
      setDriveMsg({ kind: 'err', text: e instanceof Error ? e.message : 'No se pudo iniciar la conexión' })
      setSaving(null)
    }
  }

  const desconectarGoogle = async () => {
    if (!window.confirm('¿Volver a la cuenta de servicio? Mientras la carpeta sea una «Mi unidad», '
                        + 'el bot no va a poder subir archivos.')) return
    setSaving('drive-oauth'); setDriveMsg(null)
    try {
      await apiClient.delete('/integrations/drive/oauth')
      setDriveMsg({ kind: 'ok', text: 'Desconectada. El bot vuelve a usar la cuenta de servicio.' })
      await fetchAll()
    } catch (e) {
      setDriveMsg({ kind: 'err', text: e instanceof Error ? e.message : 'No se pudo desconectar' })
    } finally { setSaving(null) }
  }

  const toggleDrive = async () => {
    if (!drive) return
    setSaving('drive'); setDriveMsg(null)
    try {
      await apiClient.put('/integrations/drive', { enabled: !drive.enabled })
      setDrive(d => d ? { ...d, enabled: !d.enabled } : d)
    } catch (e) {
      setDriveMsg({ kind: 'err', text: e instanceof Error ? e.message : 'Error' })
    } finally { setSaving(null) }
  }

  const toggle = async (key: keyof Config, newVal: string) => {
    if (!isAdmin) return
    setSaving(key); setError(null)
    try {
      await apiClient.put('/config', { [key]: newVal })
      setConfig(prev => ({ ...prev, [key]: newVal }))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al actualizar')
    } finally {
      setSaving(null)
    }
  }

  if (loading) return <LoadingSpinner label="Cargando estado de integraciones…" />

  const webhookEnabled = config.WEBHOOK_ENABLED === 'true'
  const hubspotWrite   = config.HUBSPOT_WRITE_MODE !== 'DRY_RUN' && config.HUBSPOT_WRITE_MODE !== undefined
  const outreachMode   = config.OUTREACH_MODE ?? 'MANUAL_ALLOWLIST'

  const killSwitchBtn = (label: string, key: keyof Config, activeVal: string, inactiveVal: string, currentVal?: string) => {
    const isActive = currentVal === activeVal
    return (
      <button
        disabled={!isAdmin || saving === key}
        onClick={() => void toggle(key, isActive ? inactiveVal : activeVal)}
        style={{
          padding: '7px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
          border: `1px solid ${isActive ? 'var(--c-danger-border)' : 'var(--c-brand)'}`,
          background: isActive ? 'var(--c-danger-light)' : 'var(--c-brand)',
          color: isActive ? 'var(--c-danger-ink)' : '#fff',
          cursor: isAdmin ? 'pointer' : 'not-allowed',
          opacity: saving === key ? 0.6 : 1,
        }}
      >
        {saving === key ? '…' : isActive ? `Deshabilitar ${label}` : `Habilitar ${label}`}
      </button>
    )
  }

  return (
    <div>

      {error && <ErrorAlert message={error} />}

      {!isAdmin && (
        <div style={{
          marginBottom: 16, padding: '10px 16px', borderRadius: 10,
          background: 'var(--c-info-tint)', border: '1px solid var(--c-info-border)',
          fontSize: 13, color: 'var(--c-info-ink)',
        }}>
          Solo Administradores pueden activar o desactivar integraciones.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* HubSpot */}
        <IntegrationCard
          title="HubSpot CRM"
          description="Importación de candidatos y consulta de datos de contacto en modo lectura."
          status="active"
          statusLabel="Conectado · Solo lectura"
          detail={`HUBSPOT_WRITE_MODE: ${config.HUBSPOT_WRITE_MODE ?? 'DRY_RUN'}`}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            <div style={{ display: 'flex', gap: 16 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--c-ink-muted)', marginBottom: 2 }}>MODO</div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{hubspotWrite ? 'Lectura + Escritura' : 'Solo lectura (seguro)'}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--c-ink-muted)', marginBottom: 2 }}>CANDIDATOS</div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>3 310 importados</div>
              </div>
            </div>
            {isAdmin && (
              <div style={{ display: 'flex', gap: 8 }}>
                {killSwitchBtn('escritura HubSpot', 'HUBSPOT_WRITE_MODE', 'ENABLED', 'DRY_RUN', config.HUBSPOT_WRITE_MODE)}
              </div>
            )}
          </div>
        </IntegrationCard>

        {/* n8n webhook */}
        <IntegrationCard
          title="n8n — Webhook de eventos"
          description="Recepción de eventos HubSpot retransmitidos por n8n con verificación HMAC."
          status={webhookEnabled ? 'active' : 'inactive'}
          statusLabel={webhookEnabled ? 'Habilitado' : 'Deshabilitado'}
          detail={`WEBHOOK_ENABLED: ${config.WEBHOOK_ENABLED ?? 'false'} · Ruta: POST /integrations/n8n/hubspot-events`}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <StatusDot active={webhookEnabled} />
              <span style={{ fontSize: 13, color: 'var(--c-ink-soft)' }}>
                {webhookEnabled ? 'Recibiendo eventos de n8n' : 'Sin recepción de eventos'}
              </span>
            </div>
            {isAdmin && killSwitchBtn('webhook', 'WEBHOOK_ENABLED', 'true', 'false', config.WEBHOOK_ENABLED)}
          </div>
        </IntegrationCard>

        {/* Outreach / WhatsApp saliente */}
        <IntegrationCard
          title="Contacto proactivo (Outreach)"
          description="Envío manual de mensajes WhatsApp a números en la allowlist usando plantillas aprobadas."
          status={outreachMode === 'DISABLED' ? 'inactive' : outreachMode === 'MANUAL_ALLOWLIST' ? 'warning' : 'active'}
          statusLabel={outreachMode === 'DISABLED' ? 'Deshabilitado' : outreachMode === 'MANUAL_ALLOWLIST' ? 'Allowlist manual' : 'Abierto'}
          detail={`OUTREACH_MODE: ${outreachMode} · Límite TEST: 50 / día`}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            <div style={{ fontSize: 13, color: 'var(--c-ink-soft)' }}>
              {outreachMode === 'DISABLED'
                ? 'El envío proactivo está bloqueado (kill switch activo).'
                : `Allowlist: +51 997 160 286 — único número autorizado en TEST.`}
            </div>
            {isAdmin && killSwitchBtn('outreach', 'OUTREACH_MODE', 'MANUAL_ALLOWLIST', 'DISABLED', config.OUTREACH_MODE)}
          </div>
        </IntegrationCard>

        {/* Google Drive — documentos de clientes */}
        <IntegrationCard
          title="Google Drive — Documentos de clientes"
          description="Carpeta destino donde el bot organiza los documentos recibidos por WhatsApp por cliente y tipo."
          // Un token rechazado por Google no es «Conectado»: el bot no está
          // archivando, y el estado de la tarjeta es lo primero que se lee.
          status={driveSinAutorizar ? 'warning'
            : drive?.enabled && drive?.folder_id ? 'active' : drive?.folder_id ? 'warning' : 'inactive'}
          statusLabel={driveSinAutorizar ? 'Hay que reconectar'
            : drive?.enabled && drive?.folder_id ? 'Conectado' : drive?.folder_id ? 'Pausado' : 'Sin configurar'}
          detail={drive?.folder_id
            ? (drive.folder_name
                ? `Carpeta: ${drive.folder_name}  ·  ID: ${drive.folder_id}`
                : `ID: ${drive.folder_id}  ·  Nombre no resuelto (prueba conexión)`)
            : 'Sin carpeta destino configurada'}
        >
          {drive && (
            <>
              {drive.folder_url && (
                <div style={{ fontSize: 13 }}>
                  <a href={drive.folder_url} target="_blank" rel="noreferrer"
                     style={{ color: 'var(--c-info)', fontWeight: 600 }}>
                    Abrir carpeta en Drive ↗
                  </a>
                </div>
              )}

              {driveMsg && (
                <div style={{
                  padding: '9px 13px', borderRadius: 8, fontSize: 13,
                  background: driveMsg.kind === 'ok' ? 'var(--c-brand-light)' : 'var(--c-danger-light)',
                  color:      driveMsg.kind === 'ok' ? 'var(--c-brand-ink)' : 'var(--c-danger-ink)',
                }}>{driveMsg.text}</div>
              )}

              {/* Con qué cuenta sube el bot */}
              <CuentaQueSube drive={drive} isAdmin={isAdmin} ocupado={saving === 'drive-oauth'}
                             onConectar={() => void conectarGoogle()}
                             onDesconectar={() => void desconectarGoogle()} />

              {isAdmin && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button onClick={() => void testDrive()}
                    disabled={testing || !drive.folder_id}
                    style={{
                      all: 'unset', cursor: testing || !drive.folder_id ? 'not-allowed' : 'pointer',
                      padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                      border: '1px solid var(--c-surface-strong)', color: 'var(--c-ink-soft)',
                      opacity: testing || !drive.folder_id ? 0.5 : 1,
                    }}>
                    {testing ? 'Probando…' : 'Probar conexión'}
                  </button>
                  {/* La única prueba que demuestra que el bot va a poder
                      archivar: sube un archivo de una línea y lo borra. */}
                  <button onClick={() => void testDrive(undefined, true)}
                    disabled={testing || !drive.folder_id}
                    style={{
                      all: 'unset', cursor: testing || !drive.folder_id ? 'not-allowed' : 'pointer',
                      padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                      border: '1px solid var(--c-surface-strong)', color: 'var(--c-ink-soft)',
                      opacity: testing || !drive.folder_id ? 0.5 : 1,
                    }}>
                    Probar subida
                  </button>
                  <button onClick={() => { setShowDriveEdit(true); setDriveMsg(null) }}
                    style={{
                      all: 'unset', cursor: 'pointer',
                      padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                      border: '1px solid var(--c-info)', color: 'var(--c-info-ink)',
                    }}>
                    Cambiar carpeta
                  </button>
                  {drive.folder_id && (
                    <button onClick={() => void toggleDrive()}
                      disabled={saving === 'drive'}
                      style={{
                        all: 'unset', cursor: saving === 'drive' ? 'not-allowed' : 'pointer',
                        padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                        border: `1px solid ${drive.enabled ? 'var(--c-danger-border)' : 'var(--c-brand)'}`,
                        background: drive.enabled ? 'var(--c-danger-light)' : 'var(--c-brand)',
                        color: drive.enabled ? 'var(--c-danger-ink)' : '#fff',
                        opacity: saving === 'drive' ? 0.6 : 1,
                      }}>
                      {saving === 'drive' ? '…' : drive.enabled ? 'Pausar sincronización' : 'Reanudar sincronización'}
                    </button>
                  )}
                </div>
              )}

              {/* Modal / inline editor de folder_id */}
              {showDriveEdit && (
                <div style={{
                  border: '1px solid var(--c-info-border)', borderRadius: 10,
                  padding: '14px 16px', background: 'var(--c-info-tint)',
                  display: 'flex', flexDirection: 'column', gap: 10,
                }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--c-info-deep)' }}>
                    Nueva carpeta destino
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--c-ink-muted)' }}>
                    Pega el <strong>ID</strong> de la carpeta (parte final de la URL de Drive, después de <code>/folders/</code>).
                    Asegúrate de haberla compartido con el Service Account.
                  </div>
                  <input
                    value={newFolderId}
                    onChange={e => setNewFolderId(e.target.value)}
                    placeholder="1qIVuyo0Mj6u-fDg--z04FRCNUJIymSs9"
                    style={{
                      border: '1px solid var(--c-surface-strong)', borderRadius: 7,
                      padding: '9px 12px', fontSize: 13, fontFamily: 'monospace',
                    }}
                  />
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button onClick={() => { setShowDriveEdit(false); setNewFolderId('') }}
                      style={{
                        all: 'unset', cursor: 'pointer',
                        padding: '7px 14px', borderRadius: 7, fontSize: 13,
                        border: '1px solid var(--c-surface-strong)', color: 'var(--c-ink-soft)',
                      }}>Cancelar</button>
                    <button onClick={() => void testDrive(newFolderId.trim())}
                      disabled={testing || !newFolderId.trim()}
                      style={{
                        all: 'unset', cursor: testing || !newFolderId.trim() ? 'not-allowed' : 'pointer',
                        padding: '7px 14px', borderRadius: 7, fontSize: 13, fontWeight: 600,
                        border: '1px solid var(--c-info)', color: 'var(--c-info-ink)',
                        opacity: testing || !newFolderId.trim() ? 0.5 : 1,
                      }}>Probar</button>
                    <button onClick={() => void saveDriveFolder()}
                      disabled={saving === 'drive' || !newFolderId.trim()}
                      style={{
                        all: 'unset', cursor: saving === 'drive' || !newFolderId.trim() ? 'not-allowed' : 'pointer',
                        padding: '7px 14px', borderRadius: 7, fontSize: 13, fontWeight: 700,
                        background: 'var(--c-brand)', color: '#fff',
                        opacity: saving === 'drive' || !newFolderId.trim() ? 0.5 : 1,
                      }}>
                      {saving === 'drive' ? 'Guardando…' : 'Guardar'}
                    </button>
                  </div>
                </div>
              )}

              {drive.updated_at && (
                <div style={{ fontSize: 11, color: 'var(--c-ink-muted)' }}>
                  Última actualización: {new Date(drive.updated_at).toLocaleString('es-PE')}
                  {drive.updated_by ? ` por ${drive.updated_by}` : ''}
                </div>
              )}
            </>
          )}
        </IntegrationCard>

      </div>
    </div>
  )
}
